import { AgGridTable } from '@shared/components/ui/AgGridTable/AgGridTable';
import { Avatar, AvatarFallback, AvatarImage } from '@shared/components/ui/shadcn/avatar';
import { Badge } from '@shared/components/ui/badge';
import type { ColDef } from 'ag-grid-community';
import type { RefObject } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Batch } from '../../../models/Batch';
import { BatchDropdownActions } from '../interaction/BatchDropdownActions';
import type { BatchesModalsRef } from '../modals/BatchesModals';

interface BatchesTableProps {
  batches: Batch[];
  isFetching: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  modalsRef: RefObject<BatchesModalsRef | null>;
  sorting?: { field: string; direction: 'asc' | 'desc' } | null;
  onSortingChange?: (sorting: { field: string; direction: 'asc' | 'desc' } | null) => void;
}

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  active: { bg: 'bg-bg-success', text: 'text-success' },
  expired: { bg: 'bg-bg-error', text: 'text-error' },
  depleted: { bg: 'bg-card-border light:bg-bg-warning', text: 'text-warning' },
  blocked: { bg: 'bg-card-border light:bg-bg-info', text: 'text-info' },
};

const getBatchStatus = (batch: Batch): string => {
  if (batch.blocked_at && !batch.unblocked_at) return 'blocked';
  if (batch.expiry_date && new Date(batch.expiry_date) < new Date()) return 'expired';
  if (batch.total_stock === 0) return 'depleted';
  return 'active';
};

const formatDate = (date: string | null): string => {
  if (!date) return '\u2014';
  return new Date(date).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

export const BatchesTable = ({
  batches,
  isFetching,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  modalsRef,
  sorting,
  onSortingChange,
}: BatchesTableProps) => {
  const { t } = useTranslation('batchesModule');

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        field: 'batch_number',
        headerName: t('table.batch'),
        minWidth: 200,
        sortable: true,
        cellRenderer: (params: any) => {
          const batch = params.data as Batch;
          if (!batch) return null;
          return (
            <div className="flex flex-col justify-center h-full">
              <span className="font-semibold text-sm text-text">{batch.batch_number}</span>
              {batch.supplier_reference && (
                <span className="text-xs text-complementary-text">
                  {t('table.supplier_ref')} {batch.supplier_reference}
                </span>
              )}
            </div>
          );
        },
      },
      {
        field: 'product.name',
        headerName: t('table.product'),
        minWidth: 220,
        sortable: false,
        cellRenderer: (params: any) => {
          const batch = params.data as Batch;
          if (!batch?.product) return null;
          return (
            <div className="flex items-center gap-2 h-full">
              <Avatar className="w-8 h-8 flex-shrink-0">
                <AvatarImage src={batch.product.image_url ?? undefined} alt={batch.product.name} />
                <AvatarFallback className="bg-gray-200 text-gray-700 text-xs font-medium">
                  {batch.product.name?.charAt(0)?.toUpperCase() || 'P'}
                </AvatarFallback>
              </Avatar>
              <div className="flex flex-col justify-center min-w-0">
                <span className="font-semibold text-sm text-text truncate">{batch.product.name}</span>
                <span className="text-xs text-complementary-text truncate">{batch.product.code}</span>
              </div>
            </div>
          );
        },
      },
      {
        field: 'received_at',
        headerName: t('table.received_at'),
        minWidth: 130,
        sortable: true,
        valueGetter: (params) => params.data?.received_at,
        cellRenderer: (params: any) => {
          const batch = params.data as Batch;
          if (!batch) return null;
          return (
            <div className="flex items-center h-full">
              <span className="text-sm text-text">{formatDate(batch.received_at)}</span>
            </div>
          );
        },
      },
      {
        field: 'expiry_date',
        headerName: t('table.expiry_date'),
        minWidth: 130,
        sortable: true,
        valueGetter: (params) => params.data?.expiry_date,
        cellRenderer: (params: any) => {
          const batch = params.data as Batch;
          if (!batch) return null;
          return (
            <div className="flex items-center h-full">
              <span className="text-sm text-text">{formatDate(batch.expiry_date)}</span>
            </div>
          );
        },
      },
      {
        field: 'total_stock',
        headerName: t('table.stock'),
        minWidth: 100,
        sortable: true,
        valueGetter: (params) => params.data?.total_stock ?? 0,
        cellRenderer: (params: any) => {
          const batch = params.data as Batch;
          if (!batch) return null;
          const stock = batch.total_stock ?? 0;
          const formatted =
            stock >= 1000 ? `${(stock / 1000).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')} uds.` : `${stock} uds.`;
          return (
            <div className="flex items-center h-full">
              <span className="text-sm text-text font-medium">{formatted}</span>
            </div>
          );
        },
      },
      {
        field: 'status',
        headerName: t('table.status'),
        minWidth: 110,
        sortable: false,
        cellRenderer: (params: any) => {
          const batch = params.data as Batch;
          if (!batch) return null;
          const status = getBatchStatus(batch);
          const styles = STATUS_STYLES[status] || STATUS_STYLES.active;
          return (
            <div className="flex items-center h-full">
              <Badge className={`text-xs ${styles.bg} ${styles.text}`}>{t(`status.${status}`)}</Badge>
            </div>
          );
        },
      },
      {
        field: 'actions',
        headerName: '',
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        resizable: false,
        sortable: false,
        filter: false,
        pinned: 'right',
        lockPosition: 'right',
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        cellRenderer: (params: any) => {
          const batch = params.data as Batch;
          if (!batch) return null;
          return (
            <div className="flex items-center justify-center h-full w-full">
              <BatchDropdownActions batch={batch} modalsRef={modalsRef} />
            </div>
          );
        },
      },
    ],
    [t, modalsRef],
  );

  return (
    <div
      className={`w-full transition-opacity duration-300 ${
        isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''
      }`}
    >
      <AgGridTable
        data={batches}
        columnDefs={columnDefs}
        height="calc(100vh - 250px)"
        className="rounded-lg shadow-sm"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={onSortingChange}
      />
    </div>
  );
};
